import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import './LTVAnalytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const LTVAnalytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('12');
  const [selectedDivision, setSelectedDivision] = useState('all');
  const [ltvData, setLtvData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [cacData, setCacData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const periods = [
    { value: '6', label: '6 Months' },
    { value: '12', label: '12 Months' },
    { value: '24', label: '24 Months' }
  ];

  const divisions = [
    { value: 'all', label: 'All Divisions' },
    { value: 'Home', label: 'Home' },
    { value: 'Online', label: 'Online' },
    { value: 'Clubs', label: 'Clubs' },
    { value: 'Schools', label: 'Schools' }
  ];

  useEffect(() => {
    fetchLTVData();
  }, [selectedPeriod, selectedDivision]);

  const fetchLTVData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch overview data
      const overviewResponse = await fetch(`/api/ltv/overview?period=${selectedPeriod}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!overviewResponse.ok) {
        throw new Error('Failed to fetch LTV overview data');
      }

      const overviewData = await overviewResponse.json();
      setLtvData(overviewData.data);

      // Fetch client data
      const clientResponse = await fetch(`/api/ltv/clients?division=${selectedDivision}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (clientResponse.ok) {
        const clientData = await clientResponse.json();
        setClientData(clientData.data);
      }

      // Fetch trends data
      const trendsResponse = await fetch(`/api/ltv/trends?period=${selectedPeriod}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (trendsResponse.ok) {
        const trendsData = await trendsResponse.json();
        setTrendsData(trendsData.data);
      }

      // Fetch CAC data
      const cacResponse = await fetch(`/api/ltv/cac?period=${selectedPeriod}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (cacResponse.ok) {
        const cacData = await cacResponse.json();
        setCacData(cacData.data);
      }

    } catch (err) {
      console.error('Error fetching LTV data:', err);
      setError('Failed to load LTV analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatRatio = (ratio) => {
    return ratio.toFixed(2);
  };

  if (loading) {
    return (
      <div className="ltv-analytics-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading LTV analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ltv-analytics-container">
        <div className="error-state">
          <h2>⚠️ Error Loading LTV Analytics</h2>
          <p>{error}</p>
          <button onClick={fetchLTVData} className="retry-btn">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  if (!ltvData) {
    return (
      <div className="ltv-analytics-container">
        <div className="no-data-state">
          <h2>📊 No LTV Data Available</h2>
          <p>No LTV data found for the selected period.</p>
        </div>
      </div>
    );
  }

  // Prepare chart data for division comparison
  const divisionComparisonData = {
    labels: Object.keys(ltvData.divisions).filter(div => ltvData.divisions[div].totalClients > 0),
    datasets: [
      {
        label: 'Average LTV',
        data: Object.values(ltvData.divisions)
          .filter(div => div.totalClients > 0)
          .map(div => div.avgLTV),
        backgroundColor: 'rgba(45, 47, 142, 0.8)',
        borderColor: '#2D2F8E',
        borderWidth: 1
      },
      {
        label: 'Average CAC',
        data: Object.values(ltvData.divisions)
          .filter(div => div.totalClients > 0)
          .map(div => div.avgCAC),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: '#EF4444',
        borderWidth: 1
      }
    ]
  };

  // LTV to CAC ratio data
  const ltvCacRatioData = {
    labels: Object.keys(ltvData.divisions).filter(div => ltvData.divisions[div].totalClients > 0),
    datasets: [
      {
        data: Object.values(ltvData.divisions)
          .filter(div => div.totalClients > 0)
          .map(div => div.ltvCacRatio),
        backgroundColor: [
          '#10B981', '#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  // Trends data
  const trendsChartData = trendsData ? {
    labels: trendsData.trends.map(trend => trend.month),
    datasets: [
      {
        label: 'Average LTV',
        data: trendsData.trends.map(trend => trend.avgLTV),
        borderColor: '#2D2F8E',
        backgroundColor: 'rgba(45, 47, 142, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'New Clients',
        data: trendsData.trends.map(trend => trend.totalClients),
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        fill: true,
        yAxisID: 'y1'
      }
    ]
  } : null;

  return (
    <div className="ltv-analytics-container">
      {/* Header */}
      <div className="ltv-header">
        <div className="header-content">
          <h1>💎 Lifetime Value Analytics</h1>
          <p>Client lifetime value analysis and cost of acquisition metrics</p>
        </div>
        <div className="controls">
          <div className="control-group">
            <label htmlFor="period-select">Period:</label>
            <select
              id="period-select"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="control-dropdown"
            >
              {periods.map(period => (
                <option key={period.value} value={period.value}>{period.label}</option>
              ))}
            </select>
          </div>
          <div className="control-group">
            <label htmlFor="division-select">Division:</label>
            <select
              id="division-select"
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              className="control-dropdown"
            >
              {divisions.map(division => (
                <option key={division.value} value={division.value}>{division.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => setActiveTab('clients')}
        >
          👥 Client Details
        </button>
        <button 
          className={`tab-button ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          📈 Trends
        </button>
        <button 
          className={`tab-button ${activeTab === 'cac' ? 'active' : ''}`}
          onClick={() => setActiveTab('cac')}
        >
          💰 CAC Analysis
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Key Metrics Cards */}
          <div className="ltv-metrics-grid">
            <div className="ltv-metric-card">
              <div className="metric-icon">💎</div>
              <div className="metric-content">
                <h3>Average LTV</h3>
                <p className="metric-value">{formatCurrency(ltvData.overall.avgLTV)}</p>
                <p className="metric-subtitle">Per client lifetime value</p>
              </div>
            </div>

            <div className="ltv-metric-card">
              <div className="metric-icon">📊</div>
              <div className="metric-content">
                <h3>LTV:CAC Ratio</h3>
                <p className="metric-value">{formatRatio(ltvData.overall.ltvCacRatio)}:1</p>
                <p className="metric-subtitle">Lifetime value to acquisition cost</p>
              </div>
            </div>

            <div className="ltv-metric-card">
              <div className="metric-icon">👥</div>
              <div className="metric-content">
                <h3>Total Clients</h3>
                <p className="metric-value">{formatNumber(ltvData.overall.totalClients)}</p>
                <p className="metric-subtitle">Active clients in period</p>
              </div>
            </div>

            <div className="ltv-metric-card">
              <div className="metric-icon">💰</div>
              <div className="metric-content">
                <h3>Total LTV</h3>
                <p className="metric-value">{formatCurrency(ltvData.overall.totalLTV)}</p>
                <p className="metric-subtitle">Combined lifetime value</p>
              </div>
            </div>
          </div>

          {/* Division Comparison Charts */}
          <div className="ltv-charts-section">
            <div className="ltv-chart-container">
              <h3>🏢 Division Comparison - LTV vs CAC</h3>
              <Bar 
                data={divisionComparisonData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function(value) {
                          return formatCurrency(value);
                        }
                      }
                    }
                  }
                }}
              />
            </div>

            <div className="ltv-chart-container">
              <h3>📊 LTV:CAC Ratio by Division</h3>
              <Doughnut 
                data={ltvCacRatioData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    title: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const division = context.label;
                          const ratio = context.parsed;
                          return `${division}: ${formatRatio(ratio)}:1`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Division Details Table */}
          <div className="division-details">
            <h3>📋 Division Performance Details</h3>
            <div className="division-table">
              <div className="table-header">
                <div className="table-cell">Division</div>
                <div className="table-cell">Clients</div>
                <div className="table-cell">Avg LTV</div>
                <div className="table-cell">Avg CAC</div>
                <div className="table-cell">LTV:CAC</div>
                <div className="table-cell">Avg Tenure</div>
              </div>
              {Object.entries(ltvData.divisions).map(([division, data]) => (
                <div key={division} className="table-row">
                  <div className="table-cell">{division}</div>
                  <div className="table-cell">{formatNumber(data.totalClients)}</div>
                  <div className="table-cell">{formatCurrency(data.avgLTV)}</div>
                  <div className="table-cell">{formatCurrency(data.avgCAC)}</div>
                  <div className="table-cell">
                    <span className={`ratio-badge ${data.ltvCacRatio >= 3 ? 'good' : data.ltvCacRatio >= 2 ? 'ok' : 'poor'}`}>
                      {formatRatio(data.ltvCacRatio)}:1
                    </span>
                  </div>
                  <div className="table-cell">{formatNumber(data.avgTenure)} months</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Client Details Tab */}
      {activeTab === 'clients' && clientData && (
        <div className="client-details">
          <h3>👥 Top Clients by LTV</h3>
          <div className="client-table">
            <div className="table-header">
              <div className="table-cell">Client</div>
              <div className="table-cell">Division</div>
              <div className="table-cell">Revenue</div>
              <div className="table-cell">Lessons</div>
              <div className="table-cell">Tenure</div>
              <div className="table-cell">LTV</div>
              <div className="table-cell">LTV:CAC</div>
            </div>
            {clientData.clients.slice(0, 50).map((client, index) => (
              <div key={client.clientId} className="table-row">
                <div className="table-cell">
                  <div className="client-info">
                    <div className="client-name">{client.clientName}</div>
                    <div className="client-email">{client.email}</div>
                  </div>
                </div>
                <div className="table-cell">{client.division}</div>
                <div className="table-cell">{formatCurrency(client.totalRevenue)}</div>
                <div className="table-cell">{formatNumber(client.totalLessons)}</div>
                <div className="table-cell">{formatNumber(client.clientTenureMonths)} months</div>
                <div className="table-cell">{formatCurrency(client.netLTV)}</div>
                <div className="table-cell">
                  <span className={`ratio-badge ${client.ltvCacRatio >= 3 ? 'good' : client.ltvCacRatio >= 2 ? 'ok' : 'poor'}`}>
                    {formatRatio(client.ltvCacRatio)}:1
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && trendsData && (
        <div className="trends-section">
          <h3>📈 LTV Trends Over Time</h3>
          <div className="ltv-chart-container full-width">
            <Line 
              data={trendsChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false,
                  },
                },
                scales: {
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    ticks: {
                      callback: function(value) {
                        return formatCurrency(value);
                      }
                    }
                  },
                  y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    grid: {
                      drawOnChartArea: false,
                    },
                  }
                }
              }}
            />
          </div>
        </div>
      )}

      {/* CAC Analysis Tab */}
      {activeTab === 'cac' && cacData && (
        <div className="cac-analysis">
          <h3>💰 Cost of Acquisition Analysis</h3>
          
          <div className="cac-metrics">
            <div className="cac-metric-card">
              <h4>Overall CAC</h4>
              <p className="cac-value">{formatCurrency(cacData.overall.estimatedCAC)}</p>
              <p className="cac-subtitle">Average cost per new client</p>
            </div>
            <div className="cac-metric-card">
              <h4>New Clients</h4>
              <p className="cac-value">{formatNumber(cacData.overall.newClients)}</p>
              <p className="cac-subtitle">Clients acquired in period</p>
            </div>
            <div className="cac-metric-card">
              <h4>Total Spend</h4>
              <p className="cac-value">{formatCurrency(cacData.overall.totalSpend)}</p>
              <p className="cac-subtitle">Estimated marketing spend</p>
            </div>
          </div>

          <div className="cac-by-division">
            <h4>CAC by Division</h4>
            <div className="division-table">
              <div className="table-header">
                <div className="table-cell">Division</div>
                <div className="table-cell">New Clients</div>
                <div className="table-cell">Estimated CAC</div>
                <div className="table-cell">Total Spend</div>
              </div>
              {Object.entries(cacData.byDivision).map(([division, data]) => (
                <div key={division} className="table-row">
                  <div className="table-cell">{division}</div>
                  <div className="table-cell">{formatNumber(data.newClients)}</div>
                  <div className="table-cell">{formatCurrency(data.estimatedCAC)}</div>
                  <div className="table-cell">{formatCurrency(data.totalSpend)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LTVAnalytics;
